import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@tanium/react-confirm-modal';
import { useToastNotificationsControl } from '@tanium/react-toast-notifications-context';
import { Form, FormRow } from '@tanium/react-form';
import { FormTextInput } from '@tanium/react-form-text-input';
import { AsyncStatus } from '../../hooks/useAsync';
import { useTenantDomains } from '../../hooks/useTenantDomains';

interface ModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
}

const validDomainRegex =
  /^((?!-))(xn--)?[a-z0-9][a-z0-9-_]{0,61}[a-z0-9]{0,1}\.(xn--)?([a-z0-9-]{1,61}|[a-z0-9-]{1,30}\.[a-z]{2,})$/;

export const AddDomainModal: React.FC<ModalProps> = ({ isOpen, onRequestClose }) => {
  const { t } = useTranslation();
  const { addDomain } = useTenantDomains();
  const [domainName, setDomainName] = useState('');
  const [domainNameError, setDomainNameError] = useState('');
  const { showNotification } = useToastNotificationsControl();

  useEffect(() => {
    if (addDomain.status === AsyncStatus.Success) {
      onRequestClose();
      setDomainName('');
      addDomain.reset();
      showNotification({
        type: 'success',
        contents: t('Successfully added domain'),
      });
    } else if (addDomain.status === AsyncStatus.Failure) {
      onRequestClose();
      setDomainName('');
      addDomain.reset();
      showNotification({
        type: 'error',
        contents: t('Failed to add Domain'),
      });
    }
  }, [onRequestClose, addDomain, showNotification, t]);

  // validate domainName on change
  useEffect(() => {
    if (domainName !== '' && !validDomainRegex.test(domainName)) {
      setDomainNameError('Invalid domain');
    } else {
      setDomainNameError('');
    }
  }, [domainName]);

  return (
    <ConfirmModal
      isOpen={isOpen}
      header={t('Add Domain')}
      onConfirm={async () => {
        await addDomain.execute(domainName);
      }}
      confirmLabel={t('Add')}
      isConfirmDisabled={addDomain.status === AsyncStatus.Pending || !domainName || !!domainNameError}
      onCancel={() => {
        onRequestClose();
        setDomainName('');
        addDomain.reset();
      }}
    >
      <>{t('Specify the domain to link to your Tanium Account.')}</>
      <br />
      <br />
      <Form
        initialValue={{ domainName }}
        buttons={<></>}
        showRequiredKey={false}
        onValueChange={(e) => {
          setDomainName(e.value.domainName as string);
        }}
      >
        <FormRow dataKey={'domainName'} label={t('Domain Name')} required errors={domainNameError}>
          <FormTextInput />
        </FormRow>
      </Form>
    </ConfirmModal>
  );
};
